import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Banknote, Check, Download, ExternalLink, FileText, ShieldAlert } from 'lucide-react';

/** Controls drawn per ask before the rest go behind a link. */
const DRAWN = 4;

/** Rows drawn in the unit table before the rest go behind a control. The dev troop runs to hundreds. */
const DRAWN_ROWS = 25;

import { ApiError } from '../api/client';
import { requirementsApi } from '../api/requirements';
import type {
  CompletionSummary,
  Requirement,
  ReturnedForm,
} from '../api/requirements';
import { loadEventRoster, summariseRoster } from '../api/eventRoster';
import type { EventRoster, RosterCell, RosterPerson } from '../api/eventRoster';
import { ValidationFailure } from '../api/events';
import type { FieldErrors } from '../api/events';
import { Button } from '../ui/Button';
import { SelectField, TextAreaField, TextField } from '../ui/Field';
import { ManageBlock } from '../ui/DocketRow';
import { PanelTabs } from '../ui/Tabs';
import { PeopleGrid } from '../ui/PeopleGrid';
import { PhotoLightbox } from './PhotoLightbox';
import { asLightboxItem, ReturnedForms } from './ReturnedForms';

/**
 * What the event asks of families, and — for an adult leader — who has answered.
 *
 * This is the surface that answers "has every family done the thing", which is why read receipts on
 * messages deliberately are not: a marker saying somebody opened a thread is not a returned
 * permission slip.
 *
 * **Two audiences, two tabs, and they used to be the same card — TROOP-137.** Every ask rendered
 * one block carrying, in order: what is being asked, the blank form, the *leader's* attach and
 * replace controls, this family's answers, the returned slips, the leader's three tier counts and
 * a link to the whole unit's grid. A parent scrolled past four management surfaces to find the one
 * button that was theirs, and a leader setting an event up had to work down every ask in turn to
 * see who was outstanding. The two jobs are now the two halves of {@link PanelTabs}: *Yours* is a
 * row per person you answer for, *Everyone* is the trip summary's own table plus the setup behind
 * a disclosure.
 *
 * **The member's version has no bar at all.** They get their own asks and nothing else, because the
 * server offers them nothing else — the tab is absent rather than disabled.
 *
 * Two rules from §7 are load-bearing on this screen and neither is decoration. **A link shows the
 * host it actually goes to**, because a link posted by a unit member and put in front of every
 * family is the exact pattern phishing training says to distrust. And **the evidence tiers are
 * never merged into one number**: a scan is evidence, a tick is a claim, and a leader glancing at
 * one all-green figure before a whitewater trip is making a safety decision on the difference.
 */
export function EventRequirements({
  unitId,
  eventId,
  meId,
  children,
  canManage,
  zone,
  onChanged,
  onOpenSummary,
}: {
  /** Undefined on `/family` — that absence picks the guardian's door (TROOP-116). */
  unitId?: string;
  eventId: string;
  /** The caller's own person id — one of the people they answer for. */
  meId: string;
  /**
   * The caller's own children, on the guardian's door. Empty on the member's, where the only
   * person somebody answers for is themselves.
   */
  children?: { personId: string; displayName: string }[];
  /**
   * Whether to offer the Everyone tab. Server-computed since TROOP-116, rather than the client's
   * old guess at `permissionLevel !== 'Member'` — the API is still the enforcement point.
   */
  canManage: boolean;
  /** The event's zone, so a slip's date reads as the trip's clock rather than the viewer's. */
  zone: string;
  /** Tells the docket row above to re-read its count when an ask is added or answered. */
  onChanged?: () => void;
  /** Opens the full trip summary — every column at once, with filters and paper. */
  onOpenSummary?: () => void;
}) {
  const [asks, setAsks] = useState<Requirement[] | null>(null);
  const [forms, setForms] = useState<ReturnedForm[]>([]);
  const [viewing, setViewing] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('yours');

  const load = useCallback(async () => {
    try {
      // The slips come from their own route rather than riding on the asks, because who may see
      // one is RequirementVisibilityPolicy's answer and it is not the same question as who may see
      // what is being asked. Every member reads the second; the first is the narrowest visibility
      // in the product.
      const [loaded, returned] = await Promise.all([
        requirementsApi.forEvent(unitId, eventId),
        requirementsApi.returnedForms(unitId, eventId),
      ]);

      setAsks(loaded);
      setForms(returned);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load what this event asks for.');
    }
  }, [unitId, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setBusy(true);
      setError(null);

      try {
        await action();
        await load();
        onChanged?.();
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'That did not work.');
        return false;
      } finally {
        setBusy(false);
      }
    },
    [load, onChanged],
  );

  const banner = error && (
    <p
      className="t-caption"
      role="alert"
      style={{ color: 'var(--color-status-blocked)', margin: '0 0 var(--space-3)' }}
    >
      {error}
    </p>
  );

  const yours = (
    <div>
      {banner}

      {asks !== null && asks.length === 0 && (
        <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          Nothing is being asked of families for this event.
        </p>
      )}

      {(asks ?? []).map((ask) => (
        <AskCard
          key={ask.id}
          ask={ask}
          unitId={unitId}
          eventId={eventId}
          meId={meId}
          children={children}
          canEnterForOthers={canManage}
          busy={busy}
          forms={forms.filter((f) => f.requirementId === ask.id)}
          onOpen={(form) =>
            // The index into the whole event's set, not this ask's -- so paging from one slip
            // reaches every slip on the event, which is what somebody checking a stack of them
            // actually wants.
            setViewing(forms.findIndex((f) => f.storedFileId === form.storedFileId))
          }
          onRun={run}
        />
      ))}
    </div>
  );

  return (
    // No heading: the docket row above is the heading, and a panel that restates its own row's
    // label says the same thing twice. The row also decides whether this renders at all.
    <div>
      <PanelTabs
        label="What this event asks for"
        selected={tab}
        onSelect={setTab}
        yours={yours}
        // Absent on `/family`: the unit-wide grid is RequirementVisibilityPolicy's answer and a
        // guardian is not an adult officer of the unit (rule 15a). Absent for a plain member for
        // the same reason, by way of the server's own `canManage`.
        everyone={
          canManage && unitId ? (
            <EveryoneTab
              unitId={unitId}
              eventId={eventId}
              asks={asks ?? []}
              busy={busy}
              onRun={run}
              onOpenSummary={onOpenSummary}
            />
          ) : undefined
        }
      />

      {viewing !== null && forms[viewing] && (
        <PhotoLightbox
          photos={forms.map(asLightboxItem)}
          index={viewing}
          zone={zone}
          busy={busy}
          bylinePrefix="Entered by"
          removeLabel="Take this form back"
          onIndex={setViewing}
          onClose={() => setViewing(null)}
          onRemove={(item) =>
            void run(async () => {
              // Every completion this one photograph settles, because the photograph is what is
              // being taken back. A sibling's row is authorized on its own terms, so a caller who
              // may only withdraw one of them gets exactly that.
              for (const subject of item.form.subjects) {
                await requirementsApi.withdrawHere(
                  unitId,
                  eventId,
                  item.form.requirementId,
                  subject.completionId,
                );
              }

              setViewing(null);
            })
          }
        />
      )}
    </div>
  );
}

/**
 * The unit's half — TROOP-137.
 *
 * **The trip summary's table, cropped to the asks.** Not a lookalike: {@link PeopleGrid} is the same
 * component the summary screen renders, so a leader ticking a fee off here uses the identical
 * control they use on paper day, and there is no second implementation to drift. What the summary
 * adds is the Going column, contact details, filters and print — which is why the link out sits
 * under the table rather than the table trying to grow into it.
 *
 * **The setup is behind a disclosure, under the answers.** Reading who is outstanding is the daily
 * job; adding an ask happens once. `ManageBlock`'s own rule — configuration is activated, never
 * resting — applied to the one panel that never followed it.
 *
 * **A youth officer lands on a 403 and that is correct.** They may add the ask and may not see who
 * has returned what (§3's two axes, rule 15), so the table becomes a sentence naming the rule and
 * the setup block below it stays exactly where it was.
 */
function EveryoneTab({
  unitId,
  eventId,
  asks,
  busy,
  onRun,
  onOpenSummary,
}: {
  unitId: string;
  eventId: string;
  asks: Requirement[];
  busy: boolean;
  onRun: (action: () => Promise<unknown>) => Promise<boolean>;
  onOpenSummary?: () => void;
}) {
  const [roster, setRoster] = useState<EventRoster | null>(null);
  const [refused, setRefused] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [managing, setManaging] = useState(false);

  const read = useCallback(async () => {
    try {
      setRoster(await loadEventRoster(unitId, eventId));
      setRefused(false);
      setFailed(false);
    } catch (e) {
      // Adult officers only, and the server is the judge. A youth officer who can add the ask gets
      // a 403 here — correct, and the reason this fails to a sentence rather than a banner.
      if (e instanceof ApiError && e.status === 403) setRefused(true);
      else setFailed(true);
    }
  }, [unitId, eventId]);

  useEffect(() => {
    void read();
  }, [read]);

  // Re-reads the table as well as the asks. Ticking a box here changes a row in this very grid, so
  // without the second read the control the leader just used goes back to how it was.
  const act = (action: () => Promise<unknown>) =>
    void onRun(action).then(() => read());

  const people = roster?.people ?? [];
  const drawn = showAll ? people : people.slice(0, DRAWN_ROWS);

  // The tier counts the setup block prints, taken from the very rows the table above renders --
  // TROOP-138. They used to be a fetch per ask inside `AskSetup`, on an effect that could only
  // fire on mount, so ticking a cell here left the line below reading whatever it had said when
  // the block was opened. One fact, one piece of state: `read()` moves the grid and the numbers
  // together, and no future mutation path can update one without the other.
  //
  // Over `people`, never `drawn`: the table bounds itself at 25 rows and a denominator counted
  // from what is visible is the same class of confidently wrong number.
  const summaries = useMemo(() => (roster ? summariseRoster(roster) : {}), [roster]);

  return (
    <div>
      {refused ? (
        <p
          className="t-body inline-flex items-start gap-2"
          style={{ color: 'var(--color-text-secondary)', margin: 0 }}
        >
          <ShieldAlert
            size={15}
            aria-hidden="true"
            style={{ marginTop: '0.2em', flexShrink: 0 }}
          />
          {/* Names the rule rather than implying they are missing a permission somebody could
              grant them. */}
          <span>
            Who has returned what is visible to the unit&rsquo;s adult leaders, and to each
            family&rsquo;s own parents. You can still set up what the event asks for, below.
          </span>
        </p>
      ) : failed ? (
        <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
          Could not load who has answered.
        </p>
      ) : !roster ? (
        <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          Loading who has answered...
        </p>
      ) : roster.columns.length === 0 ? (
        <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          Nothing is being asked of families yet. Add the first ask below.
        </p>
      ) : (
        <>
          <PeopleGrid
            people={drawn}
            columns={roster.columns}
            busy={busy}
            onComplete={(column, person, tier) =>
              act(() =>
                requirementsApi.complete(unitId, eventId, column.requirementId, {
                  subjectPersonId: person.personId,
                  ...(tier ? { evidenceTier: tier } : {}),
                }),
              )
            }
            onWithdraw={(column, _person, cell: RosterCell) =>
              act(() =>
                requirementsApi.withdraw(unitId, eventId, column.requirementId, cell.completionId),
              )
            }
            onReturnForm={(column, person: RosterPerson, file) =>
              act(() =>
                requirementsApi.returnForm(unitId, eventId, column.requirementId, file, [
                  person.personId,
                ]),
              )
            }
          />

          <div
            className="flex flex-wrap items-center"
            style={{ gap: 'var(--space-4)', marginTop: 'var(--space-3)' }}
          >
            {people.length > drawn.length && (
              <Button variant="secondary" onClick={() => setShowAll(true)}>
                Show {people.length - drawn.length} more
              </Button>
            )}

            {/* The same table with every column at once, the filters, the contact details and the
                paper copy. Offered rather than duplicated -- this panel is the quick pass. */}
            {onOpenSummary && (
              <button type="button" className="link-button t-caption" onClick={onOpenSummary}>
                Open the trip summary
              </button>
            )}
          </div>
        </>
      )}

      <ManageBlock
        label="Set up what this event asks for"
        open={managing}
        onToggle={() => setManaging((was) => !was)}
      >
        {asks.map((ask) => (
          <AskSetup
            key={ask.id}
            ask={ask}
            unitId={unitId}
            eventId={eventId}
            summary={summaries[ask.id] ?? null}
            busy={busy}
            onRun={onRun}
          />
        ))}

        <AddAsk
          unitId={unitId}
          eventId={eventId}
          onAdded={async () => {
            await read();
            await onRun(async () => {});
          }}
        />
      </ManageBlock>
    </div>
  );
}

/**
 * One ask as its author sees it: what it hands out, and how the three tiers are coming back.
 *
 * **The counts live here and not on the family's card.** A parent has no use for "12 of 17
 * received" about other people's children, and putting it under their own tick was most of what
 * made the old card unreadable. Three numbers, never one — see §7.
 *
 * **They arrive as a prop and are not fetched here — TROOP-138.** This component used to hold
 * its own `useState` filled from `GET /requirements/{id}/summary` by an effect keyed on identity
 * props, which could only fire on mount. So a leader ticking the confirm box, the payment or
 * *turned in* in the table above changed a row of the grid and left this line reading whatever it
 * had said when the block was opened — and because `ManageBlock` unmounts its children, closing
 * and reopening corrected it, which made a plain staleness bug look intermittent.
 * {@link EveryoneTab} already holds every row the server aggregates, so the number and the control
 * that changes it are one piece of state and cannot drift apart.
 */
function AskSetup({
  ask,
  unitId,
  eventId,
  summary,
  busy,
  onRun,
}: {
  ask: Requirement;
  unitId: string;
  eventId: string;
  /**
   * Null when the roster did not load — which for a youth officer is a 403, and correct (rule
   * 15). The tier counts are then simply absent, and the sentence explaining why is on the tab
   * above, once, rather than repeated per ask.
   */
  summary: CompletionSummary | null;
  busy: boolean;
  onRun: (action: () => Promise<unknown>) => Promise<boolean>;
}) {
  return (
    <section
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
      }}
    >
      <h4
        className="t-body-strong inline-flex items-center gap-2"
        style={{ color: 'var(--color-text-primary)', margin: 0 }}
      >
        <AskIcon kind={ask.kind} />
        {ask.title}
      </h4>

      <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
        {audience(ask.appliesTo)}
        {!ask.isRequired && ' · optional'}
      </p>

      {summary && (
        <>
          {/*
            Three numbers for a form, never one. "12 of 17 scanned; 3 on paper; 2 self-reported" is
            what a leader needs; a single merged figure is what would get somebody hurt (§7).

            **Every other kind reaches exactly one tier, so it gets one number** — and that is not
            the merge rule 15 forbids, it is the opposite of it. An acknowledgement cannot produce a
            scan or a binder entry, so the old line read "0 of 289 received · 0 of 289
            self-reported" against every kit list in the product: two figures where one of them was
            structurally always nought, and the word "received" belonging to a document nobody was
            asked for. A count is only worth printing if the state it counts is reachable.
          */}
          {summary.artifacts + summary.physicalCopies > 0 || ask.kind === 'Document' ? (
            <>
              <p
                className="t-caption tabular"
                style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-2) 0 0' }}
              >
                <strong>{summary.artifacts}</strong> of {summary.expected} received
                {summary.physicalCopies > 0 && (
                  <>
                    {' · '}
                    <strong>{summary.physicalCopies}</strong> on paper with a leader
                  </>
                )}
                {' · '}
                <strong>{summary.selfAttested}</strong> of {summary.expected} self-reported
              </p>
              <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
                A form we hold, a form a leader is carrying, and somebody saying they did it are
                different things, so they are counted separately.
              </p>
            </>
          ) : (
            <p
              className="t-caption tabular"
              style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-2) 0 0' }}
            >
              <strong>{summary.selfAttested}</strong> of {summary.expected}{' '}
              {ask.kind === 'Payment' ? 'paid' : 'confirmed'}
            </p>
          )}
        </>
      )}

      <BlankFormRow ask={ask} unitId={unitId} eventId={eventId} busy={busy} onRun={onRun} />
    </section>
  );
}

/** The create form, kept out of {@link EveryoneTab} so that component stays about reading. */
function AddAsk({
  unitId,
  eventId,
  onAdded,
}: {
  unitId: string;
  eventId: string;
  /** Re-reads the asks, the table and the docket row above. */
  onAdded: () => void | Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [kind, setKind] = useState('Acknowledge');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [appliesTo, setAppliesTo] = useState('AllParticipants');

  if (!adding) {
    return (
      <div>
        <Button variant="secondary" onClick={() => setAdding(true)}>
          Ask families for something
        </Button>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col"
      style={{
        gap: 'var(--space-4)',
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
      }}
      onSubmit={async (e) => {
        e.preventDefault();
        setFieldErrors({});
        setError(null);
        setSending(true);

        try {
          await requirementsApi.add(unitId, eventId, {
            kind,
            title,
            body: body.trim() || null,
            externalUrl: kind === 'ExternalLink' ? url : null,
            appliesTo,
          });

          setTitle('');
          setBody('');
          setUrl('');
          setAdding(false);
          await onAdded();
        } catch (failure) {
          // Field errors land on the field that caused them, which is why this call is made here
          // rather than through the panel's shared `run`: that one collapses everything into one
          // banner, and "Where it sends them" being rejected as not a URL is a sentence that
          // belongs under the box.
          if (failure instanceof ValidationFailure) setFieldErrors(failure.errors);
          else setError(failure instanceof Error ? failure.message : 'That did not work.');
        } finally {
          setSending(false);
        }
      }}
    >
      <SelectField
        label="What kind of ask"
        value={kind}
        error={fieldErrors.kind?.[0]}
        onChange={(e) => setKind(e.target.value)}
        hint="A form to return, a link to follow, or something to read and confirm."
      >
        <option value="Acknowledge">Read and confirm</option>
        <option value="Document">Return a form</option>
        <option value="ExternalLink">Do it on another site</option>
        <option value="Payment">Pay for the trip</option>
      </SelectField>

      {kind === 'Payment' && (
        // Said before the ask is created, because it changes what the leader has to write in the
        // instructions: there is no amount field, and no family will be able to tick this
        // themselves.
        <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
          Only an adult leader can mark a payment as received. Say in the instructions how much it
          is and how to hand it over.
        </p>
      )}

      <TextField
        label="What families are being asked to do"
        required
        value={title}
        error={fieldErrors.title?.[0]}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Return the medical form"
      />

      {kind === 'ExternalLink' && (
        <TextField
          label="Where it sends them"
          required
          value={url}
          error={fieldErrors.externalUrl?.[0]}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://forms.council.example/waiver"
          // Said before the link is created, because a family cannot un-click it afterwards.
          hint="Families see the address this actually goes to, beside the label. Paste the whole thing."
        />
      )}

      <TextAreaField
        label="Instructions"
        rows={3}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        hint="What to do, and anything they need to have to hand."
      />

      <SelectField
        label="Who it applies to"
        value={appliesTo}
        error={fieldErrors.appliesTo?.[0]}
        onChange={(e) => setAppliesTo(e.target.value)}
        // The case that gets forgotten, so the hint says it outright.
        hint="A waiver for a whitewater trip applies to the adults going as well."
      >
        <option value="AllParticipants">Everyone going</option>
        <option value="Youth">Young people</option>
        <option value="Adults">Adults</option>
      </SelectField>

      {error && (
        <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <Button
          variant="primary"
          type="submit"
          disabledReason={
            sending ? 'Adding it...' : title.trim() ? undefined : 'Say what is being asked for.'
          }
        >
          Add it
        </Button>
        <Button variant="secondary" onClick={() => setAdding(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * The blank form an ask hands out — TROOP-131.
 *
 * **A handout, and therefore the opposite of the returned scan.** Anybody who may read the event
 * may print this; who may see a *filled-in* one is the narrowest rule in the product. The two used
 * to live inches apart on one card and mean opposite things, which is exactly why the server
 * decides each of them separately — and why the attach control now sits in the setup block while
 * the download stays on the family's own card.
 *
 * A leader attaches it here rather than on the add form, because the ordinary case is that the
 * council's PDF turns up a week after somebody put the campout in the calendar.
 */
function BlankFormRow({
  ask,
  unitId,
  eventId,
  busy,
  onRun,
}: {
  ask: Requirement;
  unitId: string;
  eventId: string;
  busy: boolean;
  onRun: (action: () => Promise<unknown>) => Promise<boolean>;
}) {
  const fileInput = useRef<HTMLInputElement>(null);

  return (
    <div
      className="flex flex-wrap items-center"
      style={{ gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}
    >
      {ask.blankForm && (
        <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          Hands out a form &mdash; {Math.max(1, Math.round(ask.blankForm.byteSize / 1024))} KB
        </p>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="application/pdf,image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            void onRun(() => requirementsApi.attachForm(unitId, eventId, ask.id, file));
          }
          e.target.value = '';
        }}
      />

      <button
        type="button"
        className="link-button t-caption"
        disabled={busy}
        onClick={() => fileInput.current?.click()}
      >
        {ask.blankForm ? 'Replace the form' : 'Attach a form for families to print'}
      </button>

      {ask.blankForm && (
        <button
          type="button"
          className="link-button t-caption"
          disabled={busy}
          onClick={() => void onRun(() => requirementsApi.detachForm(unitId, eventId, ask.id))}
        >
          Remove it
        </button>
      )}
    </div>
  );
}

/**
 * One ask, as the person answering it sees it.
 *
 * **Nothing here belongs to the unit.** What is being asked, the form to print, and a line per
 * person this reader answers for — and then it stops. The tier counts, the completion grid and the
 * attach controls moved to the Everyone tab, which is what makes this card readable on a phone in a
 * car park by the person it was written for.
 */
function AskCard({
  ask,
  unitId,
  eventId,
  meId,
  children,
  canEnterForOthers,
  busy,
  forms,
  onOpen,
  onRun,
}: {
  ask: Requirement;
  unitId?: string;
  eventId: string;
  meId: string;
  children?: { personId: string; displayName: string }[];
  /** An adult officer entering the paper a family handed them. Not a management surface. */
  canEnterForOthers: boolean;
  busy: boolean;
  /** The slips already returned against this ask, of the ones the caller may see. */
  forms: ReturnedForm[];
  onOpen: (form: ReturnedForm) => void;
  onRun: (action: () => Promise<unknown>) => Promise<boolean>;
}) {
  const [showAllSubjects, setShowAllSubjects] = useState(false);

  const done = new Set(ask.myCompletions.map((c) => c.subjectPersonId));

  // Unanswered first, so the bound never hides the question being asked.
  const subjects = [...(children ?? []), { personId: meId, displayName: 'you' }].sort(
    (a, b) => Number(done.has(a.personId)) - Number(done.has(b.personId)),
  );

  return (
    <section
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
        marginBottom: 'var(--space-3)',
      }}
    >
      <h3
        className="t-body-strong inline-flex items-center gap-2"
        style={{ color: 'var(--color-text-primary)', margin: 0 }}
      >
        <AskIcon kind={ask.kind} />
        {ask.title}
      </h3>

      <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
        {audience(ask.appliesTo)}
        {!ask.isRequired && ' · optional'}
      </p>

      {ask.body && (
        <p
          className="t-body"
          style={{ color: 'var(--color-text-primary)', marginTop: 'var(--space-2)' }}
        >
          {ask.body}
        </p>
      )}

      {/* The blank form the ask hands out, above everything a family is asked to do about it --
          because printing it is the first step, not an afterthought. A handout, so it renders for
          everybody who can read the event; what comes back is a different question entirely. */}
      {ask.blankForm && (
        <p className="t-body" style={{ marginTop: 'var(--space-3)' }}>
          {/* A plain link, opened in a new tab. The URL is signed and short-lived and points at the
              bucket, so `download` would be ignored anyway (it is cross-origin) -- the filename is
              set on the server instead, from the ask's own title. */}
          <a href={ask.blankForm.url} target="_blank" rel="noopener noreferrer">
            <span className="inline-flex items-center gap-2">
              <Download size={14} aria-hidden="true" />
              Download the form to print
            </span>
          </a>{' '}
          <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
            &mdash; {Math.max(1, Math.round(ask.blankForm.byteSize / 1024))} KB
          </span>
        </p>
      )}

      {ask.externalUrl && (
        <p style={{ marginTop: 'var(--space-2)' }}>
          <a href={ask.externalUrl} target="_blank" rel="noopener noreferrer" className="t-body">
            Open the form
          </a>{' '}
          {/* The destination, in full and never shortened. This is the whole mitigation: a link
              labelled "the medical form" that goes somewhere else has to look like what it is. */}
          <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
            &mdash; goes to <strong>{ask.externalHost}</strong>
          </span>
        </p>
      )}

      {ask.myCompletions.length > 0 && (
        <ul
          className="flex flex-col"
          style={{
            listStyle: 'none',
            margin: 'var(--space-3) 0 0',
            padding: 0,
            gap: 'var(--space-1)',
          }}
        >
          {ask.myCompletions.map((completion) => (
            <li
              key={completion.id}
              className="t-caption"
              style={{ color: 'var(--color-status-compliant)' }}
            >
              <span className="inline-flex items-center gap-1">
                <Check size={13} aria-hidden="true" />
                {completion.subjectName || 'You'} &mdash;{' '}
                {/* The tier, in words. A scan and a tick must never render the same. */}
                {completion.evidenceTier === 'Artifact'
                  ? 'form received'
                  : completion.evidenceTier === 'PhysicalCopy'
                    ? 'on paper with a leader'
                    : ask.kind === 'Payment'
                      ? 'payment received'
                      : 'confirmed'}
              </span>
            </li>
          ))}
        </ul>
      )}

      {ask.kind === 'Payment' && (
        // The one kind a family cannot answer for themselves (TROOP-129). A control that 403s is
        // worse than no control: the sentence says who does act, which is the design system's own
        // answer for something that would otherwise render disabled with nothing to do about it.
        //
        // Said on the Yours tab even to a leader: this is where they answer as a parent, and
        // marking money in is something they do on the Everyone tab as the unit.
        <p
          className="t-caption"
          style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-3)' }}
        >
          {canEnterForOthers
            ? 'Payments are marked off under Everyone, as the money reaches you.'
            : 'A leader marks this off once the money reaches them.'}
        </p>
      )}

      {ask.kind !== 'Document' && ask.kind !== 'Payment' && (
        // One control per person this caller answers for -- themselves, and their own children.
        // A single answer for the household is the same mistake the RSVP cards refuse to make: a
        // Scout who has read the kit list and a sibling who has not are two different facts.
        //
        // Whoever still owes it comes first, and the list is bounded, because smoke residue has
        // given one seeded parent sixty-six children in one unit.
        <div className="flex flex-wrap" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
          {subjects.slice(0, showAllSubjects ? undefined : DRAWN).map((person) => (
            <Button
              key={person.personId}
              variant="secondary"
              disabledReason={busy ? 'One moment.' : undefined}
              onClick={() =>
                onRun(() =>
                  unitId
                    ? requirementsApi.complete(unitId, eventId, ask.id, {
                        subjectPersonId: person.personId,
                      })
                    : // The guardian's route takes the requirement rather than the event, and names
                      // its subject. A parent may answer for themselves as well as for a child --
                      // an AllParticipants waiver catches the adults going, and they are one.
                      requirementsApi.completeForChild(ask.id, person.personId),
                )
              }
            >
              {done.has(person.personId)
                ? `Done for ${person.displayName}`
                : subjects.length === 1
                  ? ask.kind === 'ExternalLink'
                    ? 'I have done this'
                    : 'I have read this'
                  : `Mark for ${person.displayName}`}
            </Button>
          ))}

          {subjects.length > DRAWN && !showAllSubjects && (
            <button
              type="button"
              className="link-button t-caption"
              onClick={() => setShowAllSubjects(true)}
            >
              Show {subjects.length - DRAWN} more
            </button>
          )}
        </div>
      )}

      {ask.kind === 'Document' && (
        // The artifact itself, not a word claiming one exists (TROOP-127). §7's whole argument for
        // evidence tiers is that a scan and a tick must never read the same, and the strongest
        // version of that is showing the scan.
        <ReturnedForms
          unitId={unitId}
          eventId={eventId}
          requirementId={ask.id}
          forms={forms}
          household={subjects.map((person) => ({
            personId: person.personId,
            displayName: person.displayName === 'you' ? 'You' : person.displayName,
          }))}
          canEnterForOthers={canEnterForOthers}
          busy={busy}
          onOpen={onOpen}
          // The upload has already happened; this only re-reads, so the panel and the docket row
          // above it both agree with what was just sent.
          onChanged={async () => {
            await onRun(async () => {});
          }}
        />
      )}
    </section>
  );
}

/** One icon per kind, in one place, so the family's card and the setup block cannot disagree. */
function AskIcon({ kind }: { kind: string }) {
  const Icon =
    kind === 'Document'
      ? FileText
      : kind === 'ExternalLink'
        ? ExternalLink
        : kind === 'Payment'
          ? Banknote
          : Check;

  return <Icon size={16} aria-hidden="true" />;
}

function audience(appliesTo: string): string {
  if (appliesTo === 'Youth') return 'For the young people going';
  if (appliesTo === 'Adults') return 'For the adults going';
  return 'For everyone going';
}
